import { Stack, Tooltip, Typography, Box, Divider } from '@mui/material'
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view'
import { useEffect, useMemo, useState } from 'react'
import HandymanIcon from '@mui/icons-material/Handyman'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { floatToStringWithMaxDecimal, getColorForRemaining } from '../WorkflowResource'
import { JOB_HIGHLIGHT_COLOR } from '../bomTree'

// S31/S32 (RN09.4/RN09.5, ajuste PO 2026-07-27): the row whose job chip produced the current graph
// highlight is marked with the very same blue, so it is obvious which of the tree's rows the
// highlighted node came from. Drawn as an inset shadow rather than a wider border so the row does
// not shift sideways when it becomes highlighted — the graph node uses a 3px border for this.
const JOB_HIGHLIGHT_ROW_SHADOW = `inset 0 0 0 3px ${JOB_HIGHLIGHT_COLOR}`

/**
 * Resolves the color of a BOM tree row's secondary line (name / category / QTA): red for a
 * component removed by the last import (HU-13/S44), otherwise the usual remaining-quantity color
 * code. S35's "fully assigned" green fill was dropped (ajuste PO 2026-07-27): a filled row buried
 * this very line, so the BOM tree reads exactly like the Resources tree it reuses.
 * @param {Object} params
 * @param {boolean} params.deleted
 * @param {number} params.remaining
 * @returns {string}
 */
const resolveItemTextColor = ({ deleted, remaining }) => {
  if (deleted) {
    return 'error.main'
  }
  return getColorForRemaining(remaining)
}

/**
 * Resolves the color of a BOM tree row's title (the resource code), which does not follow the
 * remaining-quantity code — only the removal (HU-13/S44).
 * @param {Object} params
 * @param {boolean} params.deleted
 * @returns {string}
 */
const resolveItemTitleColor = ({ deleted }) => {
  if (deleted) {
    return 'error.main'
  }
  return 'text.primary'
}

const WorkflowResourceItem = (props) => {
  // RN08 (enmienda PO 23/07/2026): `readOnly`, `renderItemFooter` and `isItemCompleted` let the
  // BOM tree view (HU-08) reuse this exact tree — search included via SmartPalette — without
  // drag affordances and with its own extras (HU-09 job chips, HU-11 parent indicator). The
  // defaults keep the original JobResources drag behavior untouched.
  const { section, item, getDragProperties, readOnly = false, renderItemFooter, isItemCompleted, isItemHighlighted } = props
  const { t_ } = useSmartTranslation()

  const setupDragData = (event, dragProperties) => {
    for (const [key, value] of Object.entries(dragProperties)) {
      if (value === null || value === undefined) {
        continue
      }
      event.dataTransfer.setData(key, value)
    }
    event.dataTransfer.effectAllowed = 'move'
  }

  const getIcon = (item) => {
    if (!item || !item.componentType) {
      return undefined
    }

    let IconComponent = QuestionMarkIcon
    if (item.componentType.toLowerCase() === 'comprado') IconComponent = ShoppingCartIcon
    if (item.componentType.toLowerCase() === 'fabricado') IconComponent = HandymanIcon

    return (
      <Tooltip title={item.componentType} placement='top' arrow>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0.5,
          borderRadius: 1,
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
        }}
        >
          <IconComponent fontSize='small' />
        </Box>
      </Tooltip>
    )
  }

  // HU-13/S44: a component the last BOM import removed stays visible in red and cannot be dragged
  // onto a job — it no longer exists in the imported BOM.
  const deleted = !!item.deleted
  // S35 (RN11.1): the "fully assigned" aggregation is still resolved and exposed on the row, but it
  // no longer paints it — see the note on the content styles below.
  const completed = !deleted && typeof isItemCompleted === 'function' && isItemCompleted(item)
  const color = resolveItemTextColor({ deleted, remaining: item.remaining })
  const highlighted = typeof isItemHighlighted === 'function' && isItemHighlighted(item)

  return (
    <TreeItem
      itemId={item.position + '-' + item.uuid}
      data-testid={completed ? `bom-tree-item-completed-${item.uuid}` : undefined}
      data-highlighted={highlighted ? 'true' : undefined}
      sx={{
        '& > .MuiTreeItem-groupTransition': {
          marginLeft: '20px',
          paddingLeft: '12px',
          borderLeft: '1px dashed',
          borderColor: 'grey.700',
          '&:hover': {
            borderColor: 'primary.light'
          }
        },
        // DIRECT child, never a descendant: a row's children are rendered INSIDE its own
        // `TreeItem`, so `& .MuiTreeItem-content` would style the whole subtree with the row's
        // state — selecting a parent framed every one of its descendants in blue too (and, while
        // it existed, the "fully assigned" fill painted the whole subtree). Each item applies these
        // styles to itself, so the shared ones look identical either way; only the per-row state
        // (the job highlight) actually needs the scoping.
        '& > .MuiTreeItem-content': {
          borderRadius: '6px',
          margin: '4px 0',
          padding: '0 8px',
          transition: 'all 0.2s ease-in-out',
          border: '1px solid transparent',
          // S35 (RN11.1, ajuste PO 2026-07-27): a "fully assigned" row used to be filled green. The
          // fill buried the row's own information — the name / category / QTA line and the job
          // chips sat on top of it — so it was dropped: every row keeps the plain dark look of the
          // Resources tree this component also renders, and only the removal (red text, HU-13/S44)
          // and the job highlight (blue frame, S31/S32) alter a row's appearance.
          // S31/S32: the blue mark must survive the hover rule below, or it would vanish exactly
          // when the user moves the cursor to read the row.
          boxShadow: highlighted ? JOB_HIGHLIGHT_ROW_SHADOW : undefined,
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
            boxShadow: highlighted ? JOB_HIGHLIGHT_ROW_SHADOW : undefined
          },
          '&.Mui-selected': {
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'primary.900' : 'primary.50',
            borderLeft: '3px solid',
            borderColor: 'primary.main'
          }
        }
      }}
      label={
        <Stack
          direction='row'
          alignItems='center'
          className='drag-ghost-target'
          spacing={2}
        >
          {getIcon(item, color)}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>

            <Stack direction='row' alignItems='center' gap={1}>
              <Typography variant='body2' fontWeight='600' color={resolveItemTitleColor({ deleted })} noWrap>
                {item?.resource.code}
              </Typography>
              {item.revision
                ? (
                  <Typography variant='body2' fontWeight='600' color='text.primary' noWrap sx={{ backgroundColor: (theme) => theme.palette.secondary.contrastText, padding: '0.1rem 0.3rem', borderRadius: '0.3rem' }}>
                    {item.revision}
                  </Typography>
                  )
                : null}
            </Stack>

            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              divider={<Divider orientation='vertical' flexItem sx={{ height: 10, alignSelf: 'center' }} />}
              sx={{ overflow: 'hidden' }}
            >
              <Typography variant='caption' sx={{ whiteSpace: 'nowrap', color }}>
                {`${item?.resource.name}`}
              </Typography>

              <Typography variant='caption' color={color} sx={{ whiteSpace: 'nowrap' }}>
                {`${t_('Cat')}: ${item.category}`}
              </Typography>

              <Typography variant='caption' color={color} sx={{ whiteSpace: 'nowrap' }}>
                {`${t_('QTA')}: `}
                <Typography
                  component='span'
                  variant='caption'
                  fontWeight='700'
                >
                  {floatToStringWithMaxDecimal(item.remaining)}
                </Typography>
                {` / ${item.quantity}`}
              </Typography>
            </Stack>
            {renderItemFooter
              ? (
                <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                  {renderItemFooter(item)}
                </Stack>
                )
              : null}
          </Box>
        </Stack>
      }
      draggable={!readOnly && !deleted}
      onDragStart={(event) => {
        if (readOnly || deleted) {
          event.preventDefault()
          return
        }
        const dragGhost = event.currentTarget.querySelector('.drag-ghost-target')

        if (dragGhost) {
          event.dataTransfer.setDragImage(dragGhost, 20, 20)
        }
        const dragProperties = getDragProperties(section, item)
        setupDragData(event, dragProperties)
        event.stopPropagation()
      }}
    >
      {item.children
        ? item.children.map((child) => (
          <WorkflowResourceItem
            key={child.position + '-' + child.uuid}
            section={section}
            item={child}
            getDragProperties={getDragProperties}
            readOnly={readOnly}
            renderItemFooter={renderItemFooter}
            isItemCompleted={isItemCompleted}
            isItemHighlighted={isItemHighlighted}
          />
        ))
        : null}
    </TreeItem>
  )
}

const WorkflowResourceTypes = (props) => {
  // S69 (historia 152, RN-02.6): `onItemClick` is the tree's own click callback, forwarded as-is
  // to `SimpleTreeView` — the BOM view uses it to know which ASSEMBLY the user selected. Optional:
  // without it the tree behaves exactly as before (no selection, no side effect).
  const { section, items, getDragProperties, readOnly = false, renderItemFooter, isItemCompleted, isItemHighlighted, onItemClick } = props
  const [initialLoad, setInitialLoad] = useState(false)
  const [expandedItems, setExpandedItems] = useState([])

  useEffect(() => {
    if (Array.isArray(items) && items.length > 0 && !initialLoad) {
      const allIds = items.map(item => item.position + '-' + item.uuid)
      setExpandedItems(allIds)
      setInitialLoad(true)
    }
  }, [items, initialLoad])

  const handleExpandedItemsChange = (event, itemIds) => {
    setExpandedItems(itemIds)
  }
  const normalizePosition = (position) => {
    if (!position) {
      return position
    }

    const parts = position.split('.')

    while (parts.length > 1 && Number(parts.at(-1)) === 0) {
      parts.pop()
    }

    return parts.join('.')
  }

  const itemTree = useMemo(() => {
    if (!items || !Array.isArray(items)) return []

    const tree = []
    const parentMap = {}
    const nodes = items.map(item => ({ ...item, children: [], normalizedPosition: normalizePosition(item.position) }))

    nodes.forEach(node => {
      if (!parentMap[node.normalizedPosition]) {
        parentMap[node.normalizedPosition] = node
      }
    })

    nodes.forEach(node => {
      const pos = String(node.normalizedPosition)
      const lastDotIndex = pos.lastIndexOf('.')

      if (lastDotIndex !== -1) {
        // The prefix must be normalized too: a parent stored as "390.00" is indexed as "390", so
        // the raw prefix "390.00" of a child "390.00.10" would never find it.
        const parentPosition = normalizePosition(pos.substring(0, lastDotIndex))
        if (parentMap[parentPosition]) {
          parentMap[parentPosition].children.push(node)
        } else {
          tree.push(node)
        }
      } else {
        tree.push(node)
      }
    })

    return tree
  }, [items])

  if (!itemTree || itemTree.length === 0) {
    return null
  }

  return (
    <SimpleTreeView
      expandedItems={expandedItems}
      onExpandedItemsChange={handleExpandedItemsChange}
      selectedItems={[]}
      onItemClick={onItemClick}
    >
      {itemTree.map((item) => (
        <WorkflowResourceItem
          key={item.position + '-' + item.uuid}
          section={section}
          item={item}
          getDragProperties={getDragProperties}
          readOnly={readOnly}
          renderItemFooter={renderItemFooter}
          isItemCompleted={isItemCompleted}
          isItemHighlighted={isItemHighlighted}
        />
      ))}
    </SimpleTreeView>
  )
}

export default WorkflowResourceTypes
export { resolveItemTextColor, resolveItemTitleColor }
